import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Wrench } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const when = (value) => (value ? new Date(value).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—");
const STATUS_CLASS = { success: "status-active", failed: "status-inactive", running: "status-pending", complete: "status-active", cancelled: "status-inactive" };

export function SyncStatusPanel({ status }) {
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceError, setMaintenanceError] = useState("");

  const loadMaintenance = useCallback(async () => {
    try {
      setMaintenance((await api.get("/data-administration/maintenance")).data);
    } catch (requestError) {
      setMaintenanceError(requestError?.response?.status === 403 ? "" : formatApiError(requestError));
    }
  }, []);

  useEffect(() => {
    loadMaintenance();
  }, [loadMaintenance, status?.last_success_at]);

  const runs = status?.runs || [];
  return (
    <section className="connecteam-panel" data-testid="connecteam-sync-status">
      <div className="section-heading">
        <div>
          <h2>Sync log</h2>
          <p>Every nightly (2:00 a.m. ET) and on-demand sync is recorded with its counts and errors. Only GET calls are made to Connecteam; rate limits (100/min, 10,000/day) are respected automatically.</p>
        </div>
      </div>
      <div className="requests-table-panel">
        <table className="requests-table connecteam-table" data-testid="sync-runs-table">
          <thead><tr><th>Started</th><th>Trigger</th><th>Status</th><th>Users</th><th>Sites</th><th>Shifts</th><th>Time entries</th><th>Matched / unmatched</th><th>Exceptions</th><th>API calls</th><th>Errors</th></tr></thead>
          <tbody>
            {runs.map((run) => {
              const c = run.counts || {};
              return (
                <tr key={run.id} data-testid={`sync-run-${run.id}`}>
                  <td>{when(run.started_at)}</td>
                  <td>{run.trigger}</td>
                  <td><span className={`status-chip ${STATUS_CLASS[run.status] || ""}`} data-testid={`sync-run-status-${run.id}`}>{run.status}</span></td>
                  <td>{c.users ?? "—"}<br /><small>{c.active_users ?? 0} active</small></td>
                  <td>{c.jobs ?? "—"}</td>
                  <td>{c.shifts ?? "—"}</td>
                  <td>{c.time_entries ?? "—"}<br /><small>{c.users_with_time ?? 0} people</small></td>
                  <td data-testid={`sync-run-${run.id}-matched`}>{c.matched_active_users ?? ((c.matched_by_email ?? 0) + (c.matched_by_name ?? 0))} / {c.unmatched_active_users ?? "—"}{c.newly_matched ? <><br /><small>+{c.newly_matched} new</small></> : null}</td>
                  <td>{c.exceptions_detected ?? "—"}<br /><small>{c.open_exceptions ?? 0} open</small></td>
                  <td>{run.api_calls ?? "—"}</td>
                  <td>{run.errors?.length ? <ul className="sync-errors">{run.errors.map((e, i) => <li key={i}><AlertTriangle size={12} /> {e.step}: {e.detail}</li>)}</ul> : <CheckCircle2 size={14} className="ok-icon" />}</td>
                </tr>
              );
            })}
            {!runs.length && <tr><td colSpan={11} className="provider-empty-state" data-testid="sync-runs-empty">No sync has run yet. Use “Sync now” to pull the first snapshot.</td></tr>}
          </tbody>
        </table>
      </div>
      {maintenance && (
        <details className="supervision-history" data-testid="maintenance-status">
          <summary><Wrench size={13} /> Maintenance status · last run {when(maintenance.runs?.[0]?.started_at)} ({maintenance.runs?.[0]?.status || "—"}) · {maintenance.migrations?.length ?? 0} migrations recorded</summary>
          <ul className="month-requirements">
            {(maintenance.connecteam_syncs || []).slice(0, 3).map((run) => <li key={run.id} data-testid={`maintenance-connecteam-${run.id}`}><span className={`status-chip ${STATUS_CLASS[run.status] || ""}`}>{run.status}</span> Connecteam sync ({run.trigger}) {when(run.started_at)}{run.counts ? ` — ${run.counts.users ?? 0} users, ${run.counts.time_entries ?? 0} time entries, ${run.counts.exceptions_detected ?? 0} exceptions` : ""}{run.errors?.length ? ` — ${run.errors.length} error(s)` : ""}</li>)}
            {(maintenance.runs?.[0]?.steps || []).map((step) => <li key={step.step}><span className={`status-chip ${step.status === "finished" ? "status-active" : "status-inactive"}`}>{step.status}</span> {step.step}{step.detail ? ` — ${step.detail}` : ""}</li>)}
          </ul>
        </details>
      )}
      {maintenanceError && <p className="pairing-meta">{maintenanceError}</p>}
    </section>
  );
}
